import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Stack } from '@mui/material';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { handleLoanFeesError } from 'app/pages/loanApplication/LoanApplication.utils';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { BaseStatus } from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useIsMounted from 'hooks/useIsMounted';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { checkIsHelocLoanType } from 'utils/loanApplication';
import { getPendingChangesInfo } from 'utils/loanFees';
import { getDefaultDisbursementDate } from 'utils/loanPrepaidsDates';

import { getBusinessDaysAction } from 'store/companies/actions';
import { selectClosingBusinessDays } from 'store/companies/selectors';
import { showMessageAction } from 'store/global/actions';
import {
  editLoanPrepaidsActionV2,
  getChattelDetailsAction,
  getLoanFeesSummary,
  getLoanFeesViewAction,
  getLoanPrepaidsActionV2,
  getPropertiesActionV2,
} from 'store/loanApplications/actions';
import {
  selectCommonInfoLoanApplication,
  selectEntityChangedPropsById,
  selectIsChattelLoanPurposeFromApplication,
  selectLoanFeePrepaids,
  selectLoanFeesSummary,
  selectLoanPrepaidsInterestCredit,
  selectPrepaidDataLoanFeesView,
} from 'store/loanApplications/selectors';
import {
  selectFeeProviderOptions,
  selectFeeTypesOptionsWithoutSellerPaid,
} from 'store/system/selectors';

import PendingChangesNumber from '../../../Label/PendingChangesNumber';
import TotalAmount from '../../../Label/TotalAmount';
import Controls from '../../Controls';
import useTriggerAddIfRequested from '../../hooks/useTriggerAddIfRequested';
import {
  getRowsWithLocallyAdded,
  getSchemaInterestCredit,
  getSchemaPrepaids,
} from '../Section.utils';

import InterestCredit from './InterestCredit';
import { getInterestCreditValues } from './InterestCredit/InterestCredit.utils';
import {
  getInitialPrepaids,
  getIsPerDiemChanged,
  getPrepaidInterestCreditFirstPaymentDate,
  getPrepaidsForUpdate,
} from './PrepaidSection.utils';
import PrepaidTable from './PrepaidTable';

const schema = object().shape({
  set_prepaids: getSchemaPrepaids(),
  set_interestCredit: getSchemaInterestCredit(),
});

const PrepaidSection = ({
  cdSection,
  items: viewItems,
  isLoading,
  setEditedSection,
  setIsLoading,
  parentRef,
}) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { applicationId } = useParams();

  const [{ pendingChangesNumber, isTotalAmountPending }, setPendingChangesInfo] = useState({});

  const isMounted = useIsMounted();

  const companyId = useCompanyId();

  const feesOptions = useSelector(selectFeeTypesOptionsWithoutSellerPaid);
  const feeProviderOptions = useSelector(selectFeeProviderOptions);
  const businessDays = useSelector(selectClosingBusinessDays);
  const loanViewPrepaids = useSelector(selectLoanFeePrepaids);
  const interestCredit = useSelector(selectLoanPrepaidsInterestCredit);
  const { hasInterestCredit } = interestCredit;
  const { closingDate: closeAt, fundingDate } = useSelector(selectLoanFeesSummary) || {};
  const { loanType } = useSelector(selectCommonInfoLoanApplication);
  const isChattel = useSelector(selectIsChattelLoanPurposeFromApplication);
  const loanPendingChanges = useSelector(selectEntityChangedPropsById(loanId));
  const { prepaidInterest, prepaidInterestDays } = useSelector(selectPrepaidDataLoanFeesView);

  const isHeloc = checkIsHelocLoanType(loanType);

  const {
    form,
    setForm,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    validateField,
    isFormChanged,
    validateForm,
    setIsTouched,
  } = useForm({
    set_prepaids: getInitialPrepaids({
      loanViewPrepaids,
      viewItems,
      feesOptions,
      feeProviderOptions,
    }),
    set_interestCredit: {
      hasInterestCredit: hasInterestCredit ? BaseStatus.YES : BaseStatus.NO,
      initialHasInterestCredit: hasInterestCredit ? BaseStatus.YES : BaseStatus.NO,
      manualDailyPrepaidInterest: interestCredit.manualDailyPrepaidInterest,
      initialManualDailyPrepaidInterest: interestCredit.manualDailyPrepaidInterest,
    },
  });

  const isPerDiemChanged = useMemo(
    () => getIsPerDiemChanged({ form, loanPendingChanges, isHeloc }),
    [form, isHeloc, loanPendingChanges],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      ...getConditionalArray(loanId, () => [dispatch(getLoanPrepaidsActionV2({ loanId }))]),
      dispatch(getBusinessDaysAction({ companyId })),
      dispatch(getPropertiesActionV2({ applicationId, withSubjectProperty: true })),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, loanId, companyId, applicationId]);

  // Purpose of Chattel decides whether the interest-credit funding date waits out rescission.
  useEffectSafeStrictMode(() => {
    if (isChattel) {
      dispatch(getChattelDetailsAction({ applicationId }));
    }
  }, [dispatch, isChattel, applicationId]);

  useEffect(() => {
    const initialPrepaids = getInitialPrepaids({
      loanViewPrepaids,
      viewItems,
      feesOptions,
      feeProviderOptions,
    });

    setForm(prev => ({
      set_prepaids: getRowsWithLocallyAdded({
        initialRows: initialPrepaids,
        currentRows: prev.set_prepaids,
      }),
      set_interestCredit: {
        hasInterestCredit: hasInterestCredit ? BaseStatus.YES : BaseStatus.NO,
        initialHasInterestCredit: hasInterestCredit ? BaseStatus.YES : BaseStatus.NO,
        manualDailyPrepaidInterest: interestCredit.manualDailyPrepaidInterest,
        initialManualDailyPrepaidInterest: interestCredit.manualDailyPrepaidInterest,
      },
    }));
  }, [
    loanViewPrepaids,
    viewItems,
    setForm,
    feesOptions,
    feeProviderOptions,
    hasInterestCredit,
    interestCredit.manualDailyPrepaidInterest,
  ]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    const { pendingChangesNumber, isTotalAmountPending } = getPendingChangesInfo(parentRef);
    setPendingChangesInfo({
      pendingChangesNumber: pendingChangesNumber + (isPerDiemChanged ? 1 : 0),
      isTotalAmountPending: isTotalAmountPending || isPerDiemChanged,
    });
  }, [form, parentRef, isPerDiemChanged]);

  useEffect(() => {
    setEditedSection(prev => ({ ...prev, isFormChanged, ref: parentRef }));
  }, [setEditedSection, isFormChanged, parentRef]);

  const totalAmount = useMemo(() => {
    const viewPrepaidInterestAmount = _.last(viewItems)?.amount || 0;

    const prepaidInterestAmount =
      isHeloc && _.isNumber(form.set_interestCredit.manualDailyPrepaidInterest)
        ? getInterestCreditValues({
            prepaidInterestDays,
            prepaidInterest,
            interestCreditFundingDate: getDefaultDisbursementDate(
              interestCredit.interestCreditFundingDate,
              businessDays,
            ),
            hasInterestCredit: form.set_interestCredit.hasInterestCredit,
            manualDailyPrepaidInterest: form.set_interestCredit.manualDailyPrepaidInterest,
          }).totalRaw
        : viewPrepaidInterestAmount;

    return (
      prepaidInterestAmount +
      _.sumBy(form.set_prepaids, fee =>
        fee?.pendingChangesActionType !== PendingChangesActionType.DELETED
          ? Number(fee.totalCollected || 0)
          : 0,
      )
    );
  }, [
    form,
    viewItems,
    isHeloc,
    prepaidInterest,
    prepaidInterestDays,
    interestCredit.interestCreditFundingDate,
    businessDays,
  ]);

  const onAddFee = useCallback(() => {
    setIsTouched(true);

    setForm(prev => ({
      ...prev,
      set_prepaids: [
        ...prev.set_prepaids,
        {
          isLocallyAdded: true,
          feeId: crypto.randomUUID(),
          pendingChangesActionType: PendingChangesActionType.ADDED,
        },
      ],
    }));
  }, [setForm, setIsTouched]);

  const onDeleteFee = useCallback(
    e => {
      const { feeId } = e.currentTarget.dataset;

      setIsTouched(true);

      setForm(prev => ({
        ...prev,
        set_prepaids: prev.set_prepaids.reduce((acc, fee) => {
          if (fee.feeId === feeId && fee.isLocallyAdded) {
            return acc;
          }

          acc.push(
            fee.feeId === feeId
              ? {
                  ...fee,
                  isDeleted: true,
                  pendingChangesActionType: PendingChangesActionType.DELETED,
                }
              : fee,
          );

          return acc;
        }, []),
      }));
    },
    [setForm, setIsTouched],
  );

  const onCancelForm = useCallback(() => {
    setEditedSection(null);
  }, [setEditedSection]);

  const onSubmit = useCallback(
    async e => {
      e?.preventDefault?.();

      if (!(await validateForm())) {
        return;
      }

      const successMessage =
        'Prepaids have been edited and are ready to be stored in the loan record';

      const prepaidLoanFees = getPrepaidsForUpdate(form.set_prepaids);

      const isPerDiemChanged =
        isHeloc &&
        `${form.set_interestCredit.manualDailyPrepaidInterest}` !==
          `${form.set_interestCredit.initialManualDailyPrepaidInterest}`;

      const actionData = {
        ...prepaidLoanFees,
        loanId,
        ...getConditionalObject(closeAt, () => ({
          ...interestCredit,
          interestCreditFirstPaymentDate: getPrepaidInterestCreditFirstPaymentDate({
            hasInterestCredit: form.set_interestCredit.hasInterestCredit,
            fundingDate,
            isHeloc,
            isChattel,
            storedFirstPaymentDate: interestCredit.interestCreditFirstPaymentDate,
            businessDays,
          }),
        })),
        hasInterestCredit: form.set_interestCredit.hasInterestCredit,
        ...getConditionalObject(isPerDiemChanged, {
          manualDailyPrepaidInterest: form.set_interestCredit.manualDailyPrepaidInterest,
          isManualPrepaidInterestUpdate: true,
        }),
        requestMetadata: { tabId: LoanApplicationTabId.LOAN_FEES },
      };

      setIsLoading(true);

      return await dispatch(editLoanPrepaidsActionV2(actionData))
        .then(() => {
          dispatch(getLoanFeesViewAction({ applicationId }));
          dispatch(getLoanFeesSummary({ applicationId }));
        })
        .then(() => {
          if (isMounted) {
            setIsTouched(false);
          }

          setEditedSection(null);
        })
        .then(() => {
          dispatch(showMessageAction({ message: successMessage, variant: 'success' }));
        })
        .catch(({ response }) => handleLoanFeesError({ response, dispatch }))
        .finally(() => setIsLoading(false));
    },
    [
      validateForm,
      form,
      loanId,
      closeAt,
      interestCredit,
      fundingDate,
      businessDays,
      setIsLoading,
      dispatch,
      applicationId,
      isMounted,
      setEditedSection,
      setIsTouched,
      isHeloc,
      isChattel,
    ],
  );

  useEffect(() => {
    parentRef.current.onCancelForm = onCancelForm;
    parentRef.current.onSubmit = onSubmit;
  }, [onCancelForm, onSubmit, parentRef]);

  useTriggerAddIfRequested(setEditedSection, onAddFee);

  const meta = useMemo(
    () => ({
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
    }),
    [
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
    ],
  );

  return (
    <>
      <Stack>
        <InterestCredit
          businessDays={businessDays}
          closeAt={closeAt}
          form={form.set_interestCredit}
          handleChange={handleChange}
          isDisabled={!closeAt}
          isHeloc={isHeloc}
          isLoading={isLoading}
          isPerDiemChanged={isPerDiemChanged}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <PrepaidTable
          data={form.set_prepaids}
          isLoading={isLoading}
          meta={meta}
          setIsLoading={setIsLoading}
          onAddFee={onAddFee}
          onDeleteFee={onDeleteFee}
        />
      </Stack>

      <Controls
        isFormChanged={isFormChanged}
        isLoading={isLoading}
        onCancelForm={onCancelForm}
        onSubmit={onSubmit}
      />
      {createPortal(
        <TotalAmount isPending={isTotalAmountPending} value={totalAmount} />,
        document.getElementById(`total-loan-fee-section-${cdSection}`),
      )}
      {createPortal(
        <PendingChangesNumber value={pendingChangesNumber} />,
        document.getElementById(`pending-changes-number-${cdSection}`),
      )}
    </>
  );
};

PrepaidSection.propTypes = {
  ...WithLoadingPropTypes,
  setIsLoading: PropTypes.func.isRequired,
  cdSection: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  setEditedSection: PropTypes.func.isRequired,
  parentRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

export default memo(PrepaidSection);
